import type {
  DeliveryZoneSummary,
  OperatingHoursShape,
  Store,
  StoreDetail,
  StoreStatus,
} from '../types';

import type { RemoteDeliveryZoneDto, RemoteStoreDto } from './remoteStoreTypes';

export function normalizeStoreStatus(raw: string | undefined): StoreStatus {
  const s = (raw ?? '').toUpperCase();
  if (s === 'TEMPORARILY_CLOSED' || s === 'PERMANENTLY_CLOSED' || s === 'ACTIVE') {
    return s;
  }
  if (raw?.toLowerCase() === 'open' || raw?.toLowerCase() === 'enabled') return 'ACTIVE';
  return 'ACTIVE';
}

function pickString(primary: unknown, fallback: unknown): string {
  if (typeof primary === 'string' && primary.trim().length > 0) return primary.trim();
  if (typeof fallback === 'string' && fallback.trim().length > 0) return fallback.trim();
  return '';
}

function pickNullableString(primary: unknown, fallback: unknown): string | undefined {
  const s = pickString(primary, fallback);
  return s.length > 0 ? s : undefined;
}

function parseOperatingHours(raw: unknown): OperatingHoursShape {
  if (raw === null || raw === undefined || raw === '') return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as OperatingHoursShape;
      }
    } catch {
      return {};
    }
    return {};
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as OperatingHoursShape;
  }
  return {};
}

export function mapRemoteDeliveryZone(dto: RemoteDeliveryZoneDto): DeliveryZoneSummary | null {
  const id = typeof dto.id === 'string' && dto.id.trim().length > 0 ? dto.id.trim() : '';
  if (!id) return null;
  const storeId = pickString(dto.storeId, dto.store_id);
  const polygonRaw = dto.polygonGeojson ?? dto.polygon_geojson;
  const polygonGeojson =
    typeof polygonRaw === 'string' && polygonRaw.trim().length > 0 ? polygonRaw.trim() : undefined;

  return {
    id,
    storeId: storeId || '',
    name: typeof dto.name === 'string' ? dto.name : undefined,
    isActive: Boolean(dto.isActive ?? dto.is_active ?? true),
    polygonGeojson,
    createdAt:
      typeof dto.createdAt === 'string'
        ? dto.createdAt
        : typeof dto.created_at === 'string'
          ? dto.created_at
          : undefined,
    updatedAt:
      typeof dto.updatedAt === 'string'
        ? dto.updatedAt
        : typeof dto.updated_at === 'string'
          ? dto.updated_at
          : undefined,
  };
}

export function mapRemoteStoreRow(dto: RemoteStoreDto): Store | null {
  const id = pickString(dto.id, dto.id);
  if (!id) return null;

  const storeCode = pickString(dto.storeCode, dto.store_code);
  const name = pickString(dto.name, dto.displayName) || storeCode || id;

  const maxKm = Number(dto.maxDeliveryKm ?? dto.max_delivery_km ?? 0);
  const avgPack = Number(dto.avgPackingMin ?? dto.avg_packing_min ?? 8);

  return {
    id,
    storeCode: storeCode || '—',
    name,
    slug: pickNullableString(dto.slug, undefined),
    city: pickString(dto.city, ''),
    state: pickString(dto.state, ''),
    status: normalizeStoreStatus(dto.status),
    maxDeliveryKm: Number.isFinite(maxKm) ? maxKm : 0,
    avgPackingMin: Number.isFinite(avgPack) ? Math.floor(avgPack) : 8,
    phone: pickNullableString(dto.phone, undefined),
    pincode: pickNullableString(dto.pincode, undefined),
    updatedAt:
      typeof dto.updatedAt === 'string'
        ? dto.updatedAt
        : typeof dto.updated_at === 'string'
          ? dto.updated_at
          : undefined,
    createdAt:
      typeof dto.createdAt === 'string'
        ? dto.createdAt
        : typeof dto.created_at === 'string'
          ? dto.created_at
          : undefined,
  };
}

export function mapRemoteStoreDetail(dto: RemoteStoreDto): StoreDetail | null {
  const row = mapRemoteStoreRow(dto);
  if (!row) return null;

  const zonesRaw = dto.deliveryZones ?? dto.delivery_zones ?? [];
  const zones = Array.isArray(zonesRaw)
    ? zonesRaw.map(mapRemoteDeliveryZone).filter((z): z is DeliveryZoneSummary => z !== null)
    : [];

  const lat = Number(dto.latitude ?? 0);
  const lng = Number(dto.longitude ?? 0);

  const line2 = pickNullableString(dto.addressLine2, dto.address_line2);

  const pincode = pickString(dto.pincode, '');

  return {
    ...row,
    pincode,
    addressLine1: pickString(dto.addressLine1, dto.address_line1),
    addressLine2: line2,
    latitude: Number.isFinite(lat) ? lat : 0,
    longitude: Number.isFinite(lng) ? lng : 0,
    managerId: pickNullableString(dto.managerId, dto.manager_id),
    managerName: pickNullableString(dto.managerName, dto.manager_name),
    operatingHours: parseOperatingHours(dto.operatingHours ?? dto.operating_hours),
    deliveryZones: zones,
  };
}
